import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, format, parseISO, startOfWeek } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import VisitDialog from "@/components/portal/VisitDialog";
import { PageHeader, Spinner, EmptyState, btnPrimary, btnSecondary, STATUS } from "@/components/portal/ui";

const CHIP_CLS = {
  requested: "border-l-amber-400 bg-amber-50",
  confirmed: "border-l-[#5B2C91] bg-[#5B2C91]/8",
  completed: "border-l-emerald-500 bg-emerald-50",
  cancelled: "border-l-slate-300 bg-slate-100 opacity-60",
};

function VisitChip({ visit, onOpen, onDragStart, onDragEnd }) {
  const draggable = visit.status !== "cancelled";
  return (
    <button
      type="button"
      draggable={draggable}
      onDragStart={(e) => draggable && onDragStart(e, visit)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(visit)}
      className={`group w-full rounded-lg border-l-4 px-2 py-1.5 text-left transition-shadow hover:shadow-md ${CHIP_CLS[visit.status] || CHIP_CLS.confirmed} ${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
      data-testid={`agenda-visit-${visit.id}`}
      title={`${visit.start_time}–${visit.end_time} · ${visit.patient_name} · ${visit.specialty_name}`}
    >
      <div className="flex items-center gap-1">
        {draggable && <GripVertical className="h-3 w-3 shrink-0 text-[#8A809A] opacity-0 group-hover:opacity-100" />}
        <span className="text-xs font-bold text-[#1F152E]">{visit.start_time}</span>
      </div>
      <p className={`truncate text-xs font-semibold ${visit.status === "cancelled" ? "text-[#8A809A] line-through" : "text-[#1F152E]"}`}>{visit.patient_name}</p>
      <p className="truncate text-[10px] text-[#6B6280]">{visit.specialty_name}</p>
    </button>
  );
}

function DayCell({ dayKey, isToday, isOver, visits, onOpen, onDrop, onDragOver, onDragLeave, onDragStart, onDragEnd }) {
  return (
    <div
      onDragOver={(e) => onDragOver(e, dayKey)}
      onDragLeave={() => onDragLeave(dayKey)}
      onDrop={(e) => onDrop(e, dayKey)}
      className={`flex min-h-[92px] flex-col gap-1.5 border-l border-[#5B2C91]/8 p-1.5 transition-colors ${isOver ? "bg-[#D4F84A]/25 ring-2 ring-inset ring-[#5B2C91]/30" : isToday ? "bg-[#F6F4FA]" : "bg-white"}`}
      data-testid={`agenda-cell-${dayKey}`}
    >
      {visits.map((v) => (
        <VisitChip key={v.id} visit={v} onOpen={onOpen} onDragStart={onDragStart} onDragEnd={onDragEnd} />
      ))}
    </div>
  );
}

export default function AdminAgenda() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [doctors, setDoctors] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [visits, setVisits] = useState(null);
  const [dialog, setDialog] = useState({ open: false, visit: null });
  const [dragId, setDragId] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const todayIso = format(new Date(), "yyyy-MM-dd");

  useEffect(() => {
    Promise.all([api.get("/doctors"), api.get("/specialties"), api.get("/nurses")])
      .then(([d, s, n]) => {
        setDoctors(d.data);
        setSpecialties(s.data);
        setNurses(n.data);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const loadVisits = useCallback(() => {
    const from = format(weekStart, "yyyy-MM-dd");
    const to = format(addDays(weekStart, 6), "yyyy-MM-dd");
    api.get("/visits", { params: { date_from: from, date_to: to } }).then((r) => setVisits(r.data)).catch((e) => toast.error(apiError(e)));
  }, [weekStart]);

  useEffect(() => {
    setVisits(null);
    loadVisits();
  }, [loadVisits]);

  const rows = useMemo(() => {
    if (!doctors) return [];
    const withVisits = new Set((visits || []).map((v) => v.doctor_id));
    return doctors.filter((d) => d.status === "approved" || withVisits.has(d.id));
  }, [doctors, visits]);

  const byCell = useMemo(() => {
    const map = {};
    (visits || []).forEach((v) => {
      const key = `${v.doctor_id}|${v.date}`;
      (map[key] = map[key] || []).push(v);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [visits]);

  const reschedule = async (visitId, doctorId, dateIso) => {
    const v = (visits || []).find((x) => x.id === visitId);
    if (!v || (v.doctor_id === doctorId && v.date === dateIso)) return;
    try {
      await api.put(`/visits/${visitId}`, { doctor_id: doctorId, date: dateIso });
      toast.success("Visita spostata");
      loadVisits();
    } catch (e) {
      toast.error(apiError(e));
      loadVisits();
    }
  };

  const onDragStart = (e, v) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", v.id);
    setDragId(v.id);
  };
  const onDragEnd = () => {
    setDragId(null);
    setOverKey(null);
  };
  const onDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overKey !== key) setOverKey(key);
  };
  const onDragLeave = (key) => setOverKey((k) => (k === key ? null : k));
  const onDrop = (e, key) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData("text/plain");
    const [doctorId, dateIso] = key.split("|");
    setOverKey(null);
    setDragId(null);
    if (id) reschedule(id, doctorId, dateIso);
  };

  const gridCols = { gridTemplateColumns: "180px repeat(7, minmax(132px, 1fr))" };
  const rangeLabel = `${format(weekStart, "d MMM", { locale: it })} – ${format(addDays(weekStart, 6), "d MMM yyyy", { locale: it })}`;

  return (
    <div data-testid="admin-agenda">
      <PageHeader
        eyebrow="Agenda"
        title="Agenda settimanale"
        desc="Tutte le visite della settimana per medico. Trascina una visita in un altro giorno o su un altro medico per spostarla (mantiene l'orario)."
        actions={
          <button onClick={() => setDialog({ open: true, visit: null })} className={btnPrimary} data-testid="agenda-new-visit-btn">
            <Plus className="h-4 w-4" /> Nuova visita
          </button>
        }
      />

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex items-center gap-2">
          <button onClick={() => setWeekStart((w) => addDays(w, -7))} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white ring-1 ring-[#5B2C91]/12 hover:bg-[#F6F4FA]" aria-label="Settimana precedente" data-testid="agenda-prev-week"><ChevronLeft className="h-4 w-4 text-[#5B2C91]" /></button>
          <span className="min-w-[190px] text-center text-sm font-semibold capitalize text-[#1F152E]" data-testid="agenda-range">{rangeLabel}</span>
          <button onClick={() => setWeekStart((w) => addDays(w, 7))} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white ring-1 ring-[#5B2C91]/12 hover:bg-[#F6F4FA]" aria-label="Settimana successiva" data-testid="agenda-next-week"><ChevronRight className="h-4 w-4 text-[#5B2C91]" /></button>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className={btnSecondary} data-testid="agenda-today-btn">Questa settimana</button>
          <div className="hidden items-center gap-3 text-xs text-[#6B6280] sm:flex">
            {["requested", "confirmed", "completed"].map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5">
                <span className={`h-2.5 w-2.5 rounded-sm border-l-4 ${CHIP_CLS[s]}`} />{STATUS[s].label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {doctors === null || visits === null ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : rows.length === 0 ? (
        <EmptyState title="Nessun medico da mostrare" desc="Approva un medico oppure crea una visita per popolare l'agenda." />
      ) : (
        <div className="overflow-x-auto rounded-3xl bg-white ring-1 ring-[#5B2C91]/8" data-testid="agenda-grid">
          <div className="min-w-[1100px]">
            <div className="grid border-b border-[#5B2C91]/10" style={gridCols}>
              <div className="sticky left-0 z-10 bg-white px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[#6B6280]">Medico</div>
              {days.map((d) => {
                const iso = format(d, "yyyy-MM-dd");
                const isToday = iso === todayIso;
                return (
                  <div key={iso} className={`border-l border-[#5B2C91]/8 px-2 py-3 text-center ${isToday ? "bg-[#F6F4FA]" : ""}`} data-testid={`agenda-day-head-${iso}`}>
                    <p className={`text-xs font-bold uppercase ${isToday ? "text-[#5B2C91]" : "text-[#4A4059]"}`}>{format(d, "EEE", { locale: it })}</p>
                    <p className="text-sm font-semibold text-[#1F152E]">{format(d, "dd/MM")}</p>
                  </div>
                );
              })}
            </div>

            {rows.map((doc) => (
              <div key={doc.id} className="grid border-b border-[#5B2C91]/6 last:border-b-0" style={gridCols} data-testid={`agenda-row-${doc.id}`}>
                <div className="sticky left-0 z-10 flex flex-col justify-center bg-white px-4 py-2">
                  <p className="text-sm font-semibold text-[#1F152E]">{doc.first_name} {doc.last_name}</p>
                  <p className="truncate text-[11px] text-[#8A809A]">{(doc.specialties || []).map((s) => s.name).join(" · ") || "—"}</p>
                  {doc.status !== "approved" && <span className="mt-0.5 w-fit rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">In attesa</span>}
                </div>
                {days.map((d) => {
                  const iso = format(d, "yyyy-MM-dd");
                  const key = `${doc.id}|${iso}`;
                  return (
                    <DayCell
                      key={key}
                      dayKey={key}
                      isToday={iso === todayIso}
                      isOver={overKey === key}
                      visits={byCell[key] || []}
                      onOpen={(v) => setDialog({ open: true, visit: v })}
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-3 text-xs text-[#8A809A]">Suggerimento: il trascinamento funziona da computer. Da smartphone tocca una visita per aprirla e modificarne data, orario o medico.</p>

      <VisitDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} visit={dialog.visit} specialties={specialties} doctors={doctors || []} nurses={nurses} onSaved={loadVisits} />
    </div>
  );
}
